"use client"
import * as Dialog from '@radix-ui/react-dialog';
import {
  BookOpen,
  Code,
  FileText,
  HelpCircle,
  Loader2,
  Plus,
  X
} from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useSession } from 'next-auth/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import FAQAnswerEditor from './FAQAnswerEditor';

interface BlogOption {
  id: string;
  title: string;
  slug: string;
  tags: string[];
  publishDate?: string;
  author?: string;
  editor?: string;
  contentType?: 'blog' | 'news' | 'thought-leadership';
}

interface FAQEditorProps {
  faqId?: string;
}

export default function FAQEditor({ faqId }: FAQEditorProps) {
  const searchParams = useSearchParams();
  const blogSlugFromUrl = searchParams.get('blogSlug');
  const router = useRouter();
  const { data: session } = useSession();
  const sessionEditorName = useMemo(() => {
    const name = (session as unknown as { user?: { name?: string; email?: string } })?.user?.name;
    const email = (session as unknown as { user?: { name?: string; email?: string } })?.user?.email;
    return name || email || '';
  }, [session]);
  const [formData, setFormData] = useState({
    questions: [{ id: '1', question: '', answer: '' }],
    mappedTo: '',
    mappedToBlogId: '',
    author: '',
    editor: '',
    status: 'Active',
    schemaEnabled: true,
    schemaMarkup: ''
  });
  const [blogs, setBlogs] = useState<BlogOption[]>([]);
  const [blogsLoading, setBlogsLoading] = useState(false);
  const [blogsLoaded, setBlogsLoaded] = useState(false);
  const [blogsError, setBlogsError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [createdCount, setCreatedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<'Draft' | 'Active' | null>(null);
  const urlMappingInitializedRef = useRef(false);
  const effectiveEditorName = formData.editor || (!faqId ? sessionEditorName : '');

  // const mappingOptions = [
  //   'Brand Development Service',
  //   'Logo Design Service',
  //   'Rebranding Service',
  //   'AI as a Service',
  //   'UI/UX Design Service',
  //   'Content Marketing Service',
  //   'SEO Service'
  // ];

  // Fetch content (blogs, news, thought-leadership) for dropdown
  useEffect(() => {
    const fetchBlogs = async () => {
      setBlogsLoading(true);
      setBlogsError(null);
      try {
        const response = await fetch('/api/admin/blogs/list');
        if (!response.ok) {
          throw new Error('Failed to fetch content');
        }
        const data = await response.json();
        if (data.success) {
          setBlogs(data.blogs);
        } else {
          throw new Error(data.error || 'Failed to fetch content');
        }
      } catch (err) {
        console.error('Error fetching content:', err);
        setBlogsError(err instanceof Error ? err.message : 'Failed to fetch content');
      } finally {
        setBlogsLoading(false);
        setBlogsLoaded(true);
      }
    };

    fetchBlogs();
  }, []);

  // (Authors list is not needed anymore; author is taken directly from the mapped post.)

  // Author/editor are initialized by the URL mapping effect below and otherwise
  // read from form state or the current session at render/save time.
  // If creating a new FAQ and a blogSlug is present in URL, preselect mapped content once.
  useEffect(() => {
    if (faqId || !blogSlugFromUrl || !blogsLoaded || urlMappingInitializedRef.current) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      const matchedContent = blogs.find(b => b.slug === blogSlugFromUrl);

      setFormData(prev => ({
        ...prev,
        mappedTo: blogSlugFromUrl,
        mappedToBlogId: matchedContent?.id || '',
        author: prev.author || matchedContent?.author || '',
        editor: prev.editor || matchedContent?.editor || sessionEditorName || ''
      }));

      if (!matchedContent) {
        setBlogs(prev => {
          if (prev.some(b => b.slug === blogSlugFromUrl)) {
            return prev;
          }

          return [{
            id: blogSlugFromUrl,
            title: `${blogSlugFromUrl} (Unpublished)`,
            slug: blogSlugFromUrl,
            tags: []
            // contentType is optional, so we omit it for unpublished content
          }, ...prev];
        });
      }

      urlMappingInitializedRef.current = true;
    }, 0);

    return () => window.clearTimeout(timeout);
  }, [faqId, blogSlugFromUrl, blogs, blogsLoaded, sessionEditorName]);

  // Fetch FAQ data when editing
  useEffect(() => {
    if (faqId) {
      const fetchFAQ = async () => {
        setLoading(true);
        setFetchError(null);
        try {
          const response = await fetch(`/api/admin/faqs/${faqId}`);
          if (!response.ok) {
            if (response.status === 404) {
              throw new Error('FAQ not found');
            }
            throw new Error('Failed to fetch FAQ');
          }
          const data = await response.json();
          if (data.success && data.faq) {
            const faq = data.faq;
            setFormData({
              questions: faq.questions || [{ id: '1', question: '', answer: '' }],
              mappedTo: faq.mappedTo || '',
              mappedToBlogId: faq.mappedToBlogId || '',
              author: faq.author || '',
              editor: faq.editor || sessionEditorName || '',
              status: faq.status || 'Active',
              schemaEnabled: faq.schemaEnabled !== undefined ? faq.schemaEnabled : true,
              schemaMarkup: faq.schemaMarkup || ''
            });
          } else {
            throw new Error(data.error || 'Failed to fetch FAQ');
          }
        } catch (err) {
          console.error('Error fetching FAQ:', err);
          setFetchError(err instanceof Error ? err.message : 'Failed to fetch FAQ');
        } finally {
          setLoading(false);
        }
      };

      fetchFAQ();
    }
  }, [faqId, sessionEditorName]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleQuestionChange = (id: string, field: 'question' | 'answer', value: string) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q =>
        q.id === id ? { ...q, [field]: value } : q
      )
    }));
  };

  const addNewQuestion = () => {
    const newId = Date.now().toString();
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, { id: newId, question: '', answer: '' }]
    }));
  };

  const removeQuestion = (id: string) => {
    if (formData.questions.length > 1) {
      setFormData(prev => ({
        ...prev,
        questions: prev.questions.filter(q => q.id !== id)
      }));
    }
  };


  const generateSchemaMarkup = () => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": formData.questions
        .filter(q => q.question.trim() && q.answer.trim())
        .map(q => ({
          "@type": "Question",
          "name": q.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": q.answer
          }
        }))
    };
    return JSON.stringify(schema, null, 2);
  };

  const validateForm = () => {
    // Check if at least one question-answer pair is filled
    const hasValidQA = formData.questions.some(q => q.question.trim() && q.answer.trim());
    if (!hasValidQA) {
      setSaveError('At least one question and answer pair is required');
      return false;
    }

    // Validate all filled questions have both question and answer
    for (const q of formData.questions) {
      if (q.question.trim() && !q.answer.trim()) {
        setSaveError('All questions must have corresponding answers');
        return false;
      }
      if (!q.question.trim() && q.answer.trim()) {
        setSaveError('All answers must have corresponding questions');
        return false;
      }
    }

    return true;
  };

  const handleSave = async (status: 'Draft' | 'Active' = 'Active') => {
    if (!validateForm()) return;

    // Auto-generate schema markup if enabled
    let finalSchemaMarkup = formData.schemaMarkup;
    if (formData.schemaEnabled) {
      finalSchemaMarkup = generateSchemaMarkup();
    }

    // Update formData with generated schema
    const updatedFormData = {
      ...formData,
      schemaMarkup: finalSchemaMarkup
    };

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);

    try {
      // Prepare save data - only include mappedToBlogId if it's a valid ObjectId
      // Otherwise, rely on mappedTo (slug) for validation
      const { mappedToBlogId, ...restData } = updatedFormData;
      const saveData = {
        ...restData,
        // Persist mapped editor when present, otherwise default new FAQs to the current session user.
        editor: effectiveEditorName,
        status,
        // Only include mappedToBlogId if it's a valid ObjectId (24 hex characters)
        ...(mappedToBlogId && /^[0-9a-fA-F]{24}$/.test(mappedToBlogId) ? { mappedToBlogId } : {}),
        ...(faqId && { id: faqId }) // Include ID for updates
      };

      const url = faqId ? `/api/admin/faqs/${faqId}` : '/api/admin/faqs';
      const method = faqId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(saveData)
      });

      const data = await response.json();

      if (data.success) {
        const action = faqId ? 'updated' : (status === 'Draft' ? 'saved as draft' : 'published');
        setSaveSuccess(`FAQ ${action} successfully!`);

        // Dispatch refresh event for dashboard stats
        window.dispatchEvent(new CustomEvent('faq-refresh'));

        // Update formData with generated schema
        setFormData(updatedFormData);

        // On publish, redirect to FAQ list (preserving blogSlug if available)
        if (status === 'Active') {
          // Use mappedTo (slug) first, then fallback to blogSlugFromUrl
          const targetSlug = formData.mappedTo || blogSlugFromUrl || '';
          const targetUrl = targetSlug ? `/admin/faqs?blogSlug=${targetSlug}` : '/admin/faqs';
          router.push(targetUrl);
          return;
        }

        if (!faqId) {
          // Only reset form and increment count for new FAQs on draft save
          setCreatedCount(prev => prev + 1);
          setFormData({
            questions: [{ id: '1', question: '', answer: '' }],
            mappedTo: '',
            mappedToBlogId: '',
            author: '',
            editor: '',
            status: 'Active',
            schemaEnabled: true,
            schemaMarkup: ''
          });
        }
        setTimeout(() => setSaveSuccess(null), 3000);
      } else {
        throw new Error(data.error || 'Failed to save FAQ');
      }
    } catch (err) {
      console.error('Save error:', err);
      setSaveError(err instanceof Error ? err.message : 'Failed to save FAQ');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = () => {
    setPendingStatus('Active');
    setShowConfirmModal(true);
  };

  const handleSaveDraft = () => {
    setPendingStatus('Draft');
    setShowConfirmModal(true);
  };

  const confirmSave = () => {
    if (pendingStatus) {
      setShowConfirmModal(false);
      handleSave(pendingStatus);
      setPendingStatus(null);
    }
  };

  const handleCreateAnother = () => {
    setSaveSuccess(null);
    setSaveError(null);
    // Form is already reset in handleSave, just clear any remaining state
  };

  const handleStartOver = () => {
    setFormData({
      questions: [{ id: '1', question: '', answer: '' }],
      mappedTo: '',
      mappedToBlogId: '',
      author: '',
      editor: '',
      status: 'Active',
      schemaEnabled: true,
      schemaMarkup: ''
    });
    setCreatedCount(0);
    setSaveSuccess(null);
    setSaveError(null);
  };

  // Get content type label helper
  const getContentTypeLabel = (contentType?: 'blog' | 'news' | 'thought-leadership'): string => {
    if (!contentType) {
      // Try to detect from selected content
      const slug = formData.mappedTo || blogSlugFromUrl || '';
      const blogId = formData.mappedToBlogId || '';

      if (slug || blogId) {
        const match = blogs.find(b => b.slug === slug || b.id === blogId);
        if (match?.contentType) {
          switch (match.contentType) {
            case 'news':
              return 'News & PR';
            case 'thought-leadership':
              return 'Thought & Leadership';
            default:
              return 'Blog';
          }
        }
      }
      return 'Content';
    }

    switch (contentType) {
      case 'news':
        return 'News & PR';
      case 'thought-leadership':
        return 'Thought & Leadership';
      default:
        return 'Blog';
    }
  };

  // Get current content type
  const getCurrentContentType = (): 'blog' | 'news' | 'thought-leadership' | undefined => {
    const slug = formData.mappedTo || blogSlugFromUrl || '';
    const blogId = formData.mappedToBlogId || '';

    if (slug || blogId) {
      const match = blogs.find(b => b.slug === slug || b.id === blogId);
      return match?.contentType;
    }
    return undefined;
  };

  const currentContentTypeLabel = getContentTypeLabel(getCurrentContentType());

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h2 className="text-2xl font-bold text-gray-900">
              {faqId ? 'Edit FAQ' : 'Create New FAQ'}
            </h2>
            {currentContentTypeLabel && currentContentTypeLabel !== 'Content' && (
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${getCurrentContentType() === 'news'
                ? 'bg-blue-100 text-blue-800 border border-blue-300'
                : getCurrentContentType() === 'thought-leadership'
                  ? 'bg-purple-100 text-purple-800 border border-purple-300'
                  : 'bg-green-100 text-green-800 border border-green-300'
                }`}>
                {getCurrentContentType() === 'news' && '📰 '}
                {getCurrentContentType() === 'thought-leadership' && '💡 '}
                {getCurrentContentType() === 'blog' && '📝 '}
                {currentContentTypeLabel}
              </span>
            )}
          </div>
          <p className="text-gray-600 mt-1">
            {faqId
              ? `Update the FAQ details for this ${currentContentTypeLabel !== 'Content' ? currentContentTypeLabel.toLowerCase() : 'content'}`
              : currentContentTypeLabel !== 'Content'
                ? `Create a new FAQ for ${currentContentTypeLabel.toLowerCase()}`
                : 'Fill in the details to create a new FAQ'}
          </p>
          <div className="mt-3 flex items-center gap-2 border border-gray-200 rounded-md p-2 bg-white shadow-sm w-full pr-12">
            <span className="flex items-center text-md font-medium text-gray-700">
              <BookOpen className="h-4 w-4 mr-1.5 text-blue-600" />
              Mapped To {currentContentTypeLabel}:
            </span>
            <span className="text-md text-gray-800">
              {blogsLoading
                ? `Loading ${currentContentTypeLabel.toLowerCase()}...`
                : (() => {
                  // Check mappedTo (slug) first, then mappedToBlogId, then blogSlugFromUrl
                  const slug = formData.mappedTo || blogSlugFromUrl || '';
                  const blogId = formData.mappedToBlogId || '';

                  if (!slug && !blogId) return 'Not mapped';

                  // Try to find by slug first
                  if (slug) {
                    const match = blogs.find(b => b.slug === slug);
                    if (match) return match.title;
                  }

                  // Try to find by ID
                  if (blogId) {
                    const match = blogs.find(b => b.id === blogId);
                    if (match) return match.title;
                  }

                  // Fallback to slug or ID
                  return slug || blogId;
                })()}
            </span>
          </div>
          {blogsError && (
            <p className="mt-1 text-sm text-red-600">{blogsError}</p>
          )}
          {!faqId && createdCount > 0 && (
            <p className="text-sm text-blue-600 mt-2 font-medium">
              ✅ {createdCount} FAQ{createdCount !== 1 ? 's' : ''} created in this session
            </p>
          )}
        </div>
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={() => {
              if (blogSlugFromUrl) {
                router.push(`/admin/faqs?blogSlug=${blogSlugFromUrl}`);
              } else {
                router.push('/admin/faqs');
              }
            }}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          {!faqId && createdCount > 0 && (
            <>
              <button
                type="button"
                onClick={handleCreateAnother}
                className="px-4 py-2 border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50 transition-colors flex items-center"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Another
              </button>
              <button
                type="button"
                onClick={handleStartOver}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Start Over
              </button>
            </>
          )}
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={saving || loading}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {faqId ? 'Save as Draft' : 'Save Draft'}
          </button>
          <button
            type="button"
            onClick={handlePublish}
            disabled={saving || loading}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {faqId ? 'Update FAQ' : 'Publish FAQ'}
          </button>
        </div>
      </div>

      {/* Loading State for FAQ Fetch */}
      {loading && faqId && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Loading FAQ</h3>
              <p className="text-sm text-blue-700 mt-1">Fetching FAQ data...</p>
            </div>
          </div>
        </div>
      )}

      {/* Fetch Error */}
      {fetchError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <X className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error Loading FAQ</h3>
              <p className="text-sm text-red-700 mt-1">{fetchError}</p>
              <div className="mt-2">
                <button
                  onClick={() => window.location.reload()}
                  className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Messages */}
      {saveError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <X className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{saveError}</p>
            </div>
          </div>
        </div>
      )}

      {saveSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <HelpCircle className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Success</h3>
              <p className="text-sm text-green-700 mt-1">{saveSuccess}</p>
            </div>
          </div>
        </div>
      )}

      {/* Disable form interaction while loading */}
      <div className={`grid grid-cols-1 lg:grid-cols-3 gap-6 ${loading && faqId ? 'pointer-events-none opacity-75' : ''}`}>
        {/* Main Content Area */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <HelpCircle className="h-5 w-5 mr-2 text-blue-600" />
              Basic Information
            </h3>

            <div className="space-y-4">

              {/* Questions and Answers */}
              <div className="space-y-6">
                {formData.questions.map((qa, index) => (
                  <div key={qa.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-medium text-gray-700">
                        Question {index + 1}
                      </h4>
                      {formData.questions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeQuestion(qa.id)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Remove this Q&A pair"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Question *
                        </label>
                        <input
                          type="text"
                          value={qa.question}
                          onChange={(e) => handleQuestionChange(qa.id, 'question', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                          placeholder="Enter the frequently asked question"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Answer *
                        </label>
                        <FAQAnswerEditor
                          value={qa.answer}
                          onChange={(html) => handleQuestionChange(qa.id, 'answer', html)}
                          placeholder="Provide a comprehensive answer to the question..."
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add New FAQ Button */}
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={addNewQuestion}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add New Question & Answer
                  </button>
                </div>
              </div>

            </div>
          </div>

          {/* Publishing Settings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-green-600" />
              Publishing Settings
            </h3>

            <div className="space-y-4">
              {/* Author & Editor (read-only, mirrored from mapped post) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Author
                  </label>
                  <input
                    type="text"
                    name="author"
                    value={formData.author}
                    readOnly
                    disabled
                    className="w-full border rounded-md px-3 py-2 bg-gray-100 text-gray-700 cursor-not-allowed border-gray-300"
                    placeholder="Derived from mapped content"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Author is automatically taken from the mapped blog/news/thought-leadership post.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Editor
                  </label>
                  <input
                    type="text"
                    name="editor"
                    value={effectiveEditorName}
                    disabled
                    readOnly
                    className="w-full border rounded-md px-3 py-2 bg-gray-100 text-gray-700 cursor-not-allowed border-gray-300"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Editor is automatically taken from the mapped post or your account.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Draft">Draft</option>
                </select>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <HelpCircle className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Status Guidelines
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <ul className="list-disc list-inside space-y-1">
                        <li><strong>Active:</strong> FAQ is visible to users and search engines</li>
                        <li><strong>Inactive:</strong> FAQ is hidden but preserved in the system</li>
                        <li><strong>Draft:</strong> FAQ is being worked on and not yet ready</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Schema Markup */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Code className="h-5 w-5 mr-2 text-indigo-600" />
              Schema Markup
            </h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  Enable Schema Markup
                </label>
                <input
                  type="checkbox"
                  checked={formData.schemaEnabled}
                  onChange={(e) => setFormData(prev => ({ ...prev, schemaEnabled: e.target.checked }))}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
              </div>

              {formData.schemaEnabled && (
                <div className="space-y-3">
                  <p className="text-xs text-gray-600">
                    Schema markup will be automatically generated when you save or update the FAQ. This helps search engines understand your FAQ content and may display it in rich snippets.
                  </p>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      JSON-LD Schema
                    </label>
                    <textarea
                      name="schemaMarkup"
                      value={formData.schemaMarkup}
                      onChange={handleInputChange}
                      rows={12}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs font-mono focus:ring-primary focus:border-primary"
                      placeholder="Schema markup will be automatically generated when you save..."
                      readOnly
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Dialog.Root open={showConfirmModal} onOpenChange={(open) => !saving && setShowConfirmModal(open)}>
        <Dialog.Portal>
          {/* Overlay */}
          <Dialog.Overlay className="fixed inset-0 z-[1000] bg-black/40 backdrop-blur-sm" />

          {/* Content */}
          <Dialog.Content className="fixed z-[1001] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md rounded-2xl bg-white shadow-xl border border-gray-200 overflow-hidden">
            <div className={`${saving ? 'opacity-75 pointer-events-none' : ''} transition-all`}>
              {/* Header */}
              <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/70">
                <h3 className="text-lg font-semibold text-gray-900">
                  {faqId ? 'Update FAQ' : pendingStatus === 'Active' ? 'Publish FAQ' : 'Save as Draft'}
                </h3>
                <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                  {faqId
                    ? 'Are you sure you want to update this FAQ? The schema markup will be automatically generated.'
                    : pendingStatus === 'Active'
                      ? 'Are you sure you want to publish this FAQ? The schema markup will be automatically generated and the FAQ will be visible to users.'
                      : 'Are you sure you want to save this FAQ as a draft? The schema markup will be automatically generated.'}
                </p>
              </div>

              {/* FAQ Info Section */}
              <div className="px-6 py-4 space-y-3">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">FAQ Preview</p>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <div>
                      <p className="text-xs text-gray-500">Questions:</p>
                      <p className="text-sm font-medium text-gray-900">
                        {formData.questions.filter(q => q.question.trim()).length} question(s)
                      </p>
                    </div>
                    {(formData.mappedTo || formData.mappedToBlogId) && (
                      <div>
                        <p className="text-xs text-gray-500">Mapped To:</p>
                        <p className="text-sm font-medium text-gray-900">
                          {(() => {
                            const slug = formData.mappedTo || '';
                            const blogId = formData.mappedToBlogId || '';

                            if (slug) {
                              const match = blogs.find(b => b.slug === slug);
                              if (match) return match.title;
                            }

                            if (blogId) {
                              const match = blogs.find(b => b.id === blogId);
                              if (match) return match.title;
                            }

                            return slug || blogId;
                          })()}
                        </p>
                      </div>
                    )}
                    {formData.schemaEnabled && (
                      <div>
                        <p className="text-xs text-gray-500">Schema Markup:</p>
                        <p className="text-sm font-medium text-green-600">Will be auto-generated</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowConfirmModal(false);
                    setPendingStatus(null);
                  }}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmSave}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {faqId ? 'Update FAQ' : pendingStatus === 'Active' ? 'Publish FAQ' : 'Save Draft'}
                </button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>

  );
}
